import { useState, useEffect, useCallback } from 'react';
import { getCustomerNotes, updateCustomerNotes } from '../services/customerNotesApi';

export interface UseCustomerNotesResult {
  notes: string | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  update: (notes: string) => Promise<void>;
}

export function useCustomerNotes(
  dataverseCustomerId: string | null
): UseCustomerNotesResult {
  const [notes, setNotes] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNotes = useCallback(async (signal?: AbortSignal) => {
    if (!dataverseCustomerId) {
      setNotes(null);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCustomerNotes(dataverseCustomerId, signal);
      if (signal?.aborted) return;
      setNotes(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load notes');
      setNotes(null);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [dataverseCustomerId]);

  useEffect(() => {
    const controller = new AbortController();
    loadNotes(controller.signal);
    return () => controller.abort();
  }, [loadNotes]);

  const refresh = useCallback(() => {
    loadNotes();
  }, [loadNotes]);

  const update = useCallback(async (newNotes: string) => {
    if (!dataverseCustomerId) throw new Error('No customer selected');
    await updateCustomerNotes(dataverseCustomerId, newNotes);
    setNotes(newNotes);
  }, [dataverseCustomerId]);

  return { notes, isLoading, error, refresh, update };
}
